import React, { useState, useMemo } from 'react';
import { useT } from '../i18n.js';
// ⚠ MODIFIED FROM PORT — for the invoice document download + change password
import { api, downloadInvoiceDocument, downloadInvoiceAttachment, downloadCsv, fileToBase64, formatBytes } from '../api.js';
import { compressImage } from '../upload.js';   // 2026-09-16 photo compression before KYC upload
import { BANKS, BILLING_STATUS_META, CATEGORIES, MY_BILLS, SUPPLIERS, bankAccountDigits, bankAccountPlaceholder, catOf, fmtTHB, netPayableOf } from './data.jsx';
import { IconArrow, IconBank, IconBell, IconBuilding, IconCal, IconCheck, IconChevron, IconChevronDn, IconClock, IconDots, IconDownload, IconExt, IconEye, IconFile, IconFilter, IconLock, IconPlus, IconReceipt, IconSearch, IconSettings, IconShield, IconTag, IconUpload, IconUsers, IconX } from './icons.jsx';
import { Avatar, CategoryTag, ChipMenu, StatusPill, padThaiId } from './screens-internal.jsx';
import { EditInvoiceModal } from './modals.jsx';

import { useState as useStateEx, useMemo as useMemoEx, useEffect as useEffectEx } from 'react';

/* ============================================================
   SCREEN — BILLING INBOX (Internal / Finance)
   ============================================================ */
const INBOX_BILLS = [
  { id: "INV-2026-0518", vendor: "Tinker Post Bangkok",   vid: "VND-1042", project: "Toyota Bz4X campaign",   po: "PO-MR-3829", amount: 178880, age: "2h",  submitted: "May 17 · 09:48", status: "pm-review",      owner: "Ploy V.",  flag: null },
  { id: "INV-2026-0512", vendor: "Northstar Print Co.",   vid: "VND-1037", project: "AIS taxi takeover Q2",   po: "PO-MR-3770", amount:  64200, age: "5h",  submitted: "May 17 · 06:12", status: "pm-review",      owner: "Tee S.",   flag: null },
  { id: "INV-2026-0509", vendor: "Aurora Media Group",     vid: "VND-1051", project: "Honda CR-V dealer push", po: "PO-MR-3841", amount: 312000, age: "1d",  submitted: "May 16 · 14:30", status: "finance-verify", owner: "Ananda V.",flag: "high-value" },
  { id: "INV-2026-0506", vendor: "BKK Talent Network",     vid: "VND-0998", project: "Lazada flash event",     po: "PO-MR-3744", amount:  41500, age: "1d",  submitted: "May 16 · 11:02", status: "finance-verify", owner: "Ananda V.",flag: null },
  { id: "INV-2026-0501", vendor: "Krungsri auto loan",     vid: "VND-1199", project: "Bangkok Bank Q2",        po: "PO-MR-3890", amount:  34000, age: "2d",  submitted: "May 15 · 16:18", status: "rejected",       owner: "Ananda V.",flag: null },
  { id: "INV-2026-0492", vendor: "Mistine talent agency",  vid: "VND-1255", project: "Mistine influencer set", po: "PO-MR-3871", amount: 102400, age: "5d",  submitted: "May 12 · 09:40", status: "disputed",       owner: "Mod K.",   flag: "supplier-replied" },
  { id: "INV-2026-0488", vendor: "Rise & Crew Production", vid: "VND-1108", project: "Mistine influencer set", po: "PO-MR-3855", amount:  58000, age: "5d",  submitted: "May 12 · 08:14", status: "approved-payment",owner:"Ananda V.",flag: null },
  { id: "INV-2026-0484", vendor: "Velocity Logistics",     vid: "VND-1140", project: "Bangkok Bank Q2",        po: "PO-MR-3855", amount:  18400, age: "6d",  submitted: "May 11 · 17:22", status: "approved-payment",owner:"Ananda V.",flag: null },
  { id: "INV-2026-0479", vendor: "Castline Talent Co.",    vid: "VND-1255", project: "Honda CR-V dealer push", po: "PO-MR-3841", amount:  84200, age: "7d",  submitted: "May 10 · 11:14", status: "scheduled",      owner: "Ananda V.",flag: null },
  { id: "INV-2026-0466", vendor: "Bright Lens Productions",vid: "VND-1248", project: "Bangkok Bank Q2",        po: "PO-MR-3855", amount:  58000, age: "9d",  submitted: "May 08 · 10:08", status: "scheduled",      owner: "Ananda V.",flag: null },
  { id: "INV-2026-0445", vendor: "Echo Audio Post",        vid: "VND-1271", project: "Honda CR-V dealer push", po: "PO-MR-3841", amount: 128500, age: "13d", submitted: "May 04 · 09:52", status: "paid",           owner: "Ananda V.",flag: null },
  { id: "INV-2026-0428", vendor: "Frame & Field Studio",   vid: "VND-1166", project: "Toyota Bz4X campaign",   po: "PO-MR-3829", amount:  76800, age: "16d", submitted: "May 01 · 14:18", status: "paid",           owner: "Ananda V.",flag: null },
];

// ⚠ NEW (2026-07-20) — date helpers for the Submitted / Scheduled-pay columns.
// Normalise any date-ish value (ISO datetime like submitted_at, or a plain
// YYYY-MM-DD like payment_scheduled) to a YYYY-MM-DD string so it can be sorted
// and range-compared lexically. Returns '' for missing/unparseable values.
function toDayIso(v) {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d) ? "" : d.toISOString().slice(0, 10);
}
// Compact table-cell rendering — "17 May" (drops the year when it's this year).
function fmtDayCell(v) {
  const iso = toDayIso(v);
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "";
  const opts = d.getFullYear() === new Date().getFullYear()
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "2-digit" };
  return d.toLocaleDateString(undefined, opts);
}
// The scheduled-payment date lives in the invoice's payment_scheduled column
// (older writes used scheduled_date — accept either).
const schedPayOf = (b) => b.payment_scheduled || b.scheduled_date || "";

// ⚠ NEW (2026-07-20) — From/To date-range filter chip, styled to match ChipMenu.
// Used in the billing toolbar to filter by submitted or scheduled-payment date.
function DateRangeChip({ label, from, to, onChange, align = "left" }) {
  const t = useT();
  const [open, setOpen] = useStateEx(false);
  const active = !!(from || to);
  const fmt = (v) => { const d = new Date(v + "T00:00:00"); return isNaN(d) ? v : d.toLocaleDateString(undefined, { day: "numeric", month: "short" }); };
  const chipText = active
    ? (from && to ? `${fmt(from)} – ${fmt(to)}` : from ? `≥ ${fmt(from)}` : `≤ ${fmt(to)}`)
    : label;
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button className={"chip " + (active ? "active" : "")} onClick={() => setOpen(o => !o)}>
        <IconCal size={12} style={{ marginRight: 6 }}/>{chipText}<IconChevronDn size={10} style={{ opacity: 0.55, marginLeft: 2 }}/>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }}/>
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", [align]: 0, zIndex: 201,
            minWidth: 214, background: "var(--bg-elev-2, var(--bg-secondary))",
            border: "1px solid var(--border-default)", borderRadius: 10,
            boxShadow: "var(--elevation-3)", padding: 12,
          }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10 }}>{label}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontSize: 12, color: "var(--text-secondary)" }}>
                {t("From")}
                <input type="date" className="input" style={{ width: 150 }} value={from || ""} max={to || undefined}
                  onChange={(e) => onChange(e.target.value, to)}/>
              </label>
              <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontSize: 12, color: "var(--text-secondary)" }}>
                {t("To")}
                <input type="date" className="input" style={{ width: 150 }} value={to || ""} min={from || undefined}
                  onChange={(e) => onChange(from, e.target.value)}/>
              </label>
            </div>
            {active && (
              <button className="chip" style={{ marginTop: 10 }} onClick={() => { onChange("", ""); setOpen(false); }}>
                <IconX size={11}/> {t("Clear")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function ScreenBillingInbox({ invoices, onStatusChange, onPmApprove, onRefresh, role, staffEmail, onDelete, canDelete }) {
  const t = useT();
  // ⚠ MODIFIED FROM PORT — accepts live invoices + onStatusChange callback.
  const bills = invoices && invoices.length ? invoices : (invoices ? [] : INBOX_BILLS);
  // Separation of duties — mirror the backend so users only see steps they can do.
  const STATUS_ROLES = {
    'finance-verify':   ['admin', 'pm'],
    'approved-payment': ['admin', 'finance'],
    'scheduled':        ['admin', 'finance'],
    'paid':             ['admin', 'finance'],
    'rejected':         ['admin', 'pm', 'finance'],
    'disputed':         ['admin', 'pm', 'finance'],
  };
  const canDo = (status) => { const a = STATUS_ROLES[status]; return !a || !role || a.indexOf(role) !== -1; };
  const [tab, setTab] = useStateEx("all");
  const [openId, setOpenId] = useStateEx(bills[0]?.id);
  // ⚠ FIX (2026-06-01) — invoices load async, so bills is [] on first render and
  // openId starts undefined → the detail panel renders blank until a manual click.
  // Auto-select the first row once data arrives (or if the open row drops out).
  useEffectEx(() => {
    if (bills.length && !bills.some(b => b.id === openId)) setOpenId(bills[0].id);
  }, [bills.length, openId]);
  // ⚠ NEW (2026-07-20) — the detail panel now floats OVER the table as a
  // dismissable overlay (was a fixed 440px column that squeezed the table). It's
  // hidden by default so the table gets the full width, and opens when a row is
  // selected. Esc closes it.
  const [panelOpen, setPanelOpen] = useStateEx(false);
  // Panel width, reused as the table's right-hand scroll slack when open so the
  // columns it covers can be scrolled out from under it.
  const PANEL_W = "min(440px, 94vw)";
  const openRow = (id) => { setOpenId(id); setPanelOpen(true); };
  useEffectEx(() => {
    if (!panelOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setPanelOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen]);
  const [note, setNote] = useStateEx("");
  const [busy, setBusy] = useStateEx(null);
  const [query, setQuery] = useStateEx("");
  // ⚠ NEW (2026-06-05) — Account Payable routing. When Finance approves an
  // invoice to pay, it's written into the chosen company's AP sheet. Default to
  // the approver's own company (by email domain); they can override per-invoice.
  const defaultCompany = useMemoEx(() => {
    const d = String(staffEmail || '').split('@')[1] || '';
    if (d === 'brandstorm.co.th' || d === 'brandstorm.global') return 'brandstorm';
    if (d === 'motionreach.co.th') return 'motionreach';
    return '';
  }, [staffEmail]);
  const [apCompany, setApCompany] = useStateEx(defaultCompany);
  // Reset the AP company to the approver's default whenever the open invoice
  // changes (or the default resolves once staffEmail loads). Must come AFTER the
  // defaultCompany/apCompany declarations above — its deps array is evaluated at
  // render time, so referencing them earlier would hit the temporal dead zone.
  useEffectEx(() => { setApCompany(defaultCompany); }, [defaultCompany, openId]);
  // ⚠ NEW (2026-06-08) — Expense Main Category for the AP row. Required at the
  // approve-to-pay step and loaded live from the chosen company's finance sheet
  // (the category list differs per company). Resets whenever the company changes.
  const [apCategory, setApCategory] = useStateEx('');
  const [catOptions, setCatOptions] = useStateEx([]);
  const [catLoading, setCatLoading] = useStateEx(false);
  // ⚠ NEW (2026-06-17) — payment date chosen when marking an invoice scheduled.
  // Defaults to today; written to the invoice AND the AP sheet's Due Date column.
  const [payDate, setPayDate] = useStateEx(() => new Date().toISOString().slice(0, 10));
  useEffectEx(() => { setPayDate(new Date().toISOString().slice(0, 10)); }, [openId]);
  // ⚠ NEW (2026-06-17) — edit an invoice's submitted details (amount/dates/PO…).
  const [editInvoice, setEditInvoice] = useStateEx(null);
  useEffectEx(() => {
    setApCategory('');
    if (!apCompany) { setCatOptions([]); return; }
    let live = true;
    setCatLoading(true);
    api.listExpenseCategories(apCompany)
      .then(r => { if (live) setCatOptions((r && r.categories) || []); })
      .catch(() => { if (live) setCatOptions([]); })
      .finally(() => { if (live) setCatLoading(false); });
    return () => { live = false; };
  }, [apCompany]);
  // ⚠ NEW (2026-05-31) — working toolbar filters
  const [fVendor, setFVendor]   = useStateEx("all");
  const [fProject, setFProject] = useStateEx("all");
  const [fOwner, setFOwner]     = useStateEx("all");
  const [fAmount, setFAmount]   = useStateEx("all");
  // ⚠ NEW (2026-07-20) — date-range filters (YYYY-MM-DD, "" = open bound) for the
  // Submitted and Scheduled-payment columns.
  const [subFrom, setSubFrom] = useStateEx("");
  const [subTo, setSubTo]     = useStateEx("");
  const [schFrom, setSchFrom] = useStateEx("");
  const [schTo, setSchTo]     = useStateEx("");
  // ⚠ NEW (2026-07-20) — clickable-header sort, mirroring the Suppliers screen.
  // Base comparators are ascending; sortDir applies the direction so every header
  // click is a simple asc↔desc toggle. Default: newest-submitted first.
  const [sortKey, setSortKey] = useStateEx("submitted");
  const [sortDir, setSortDir] = useStateEx("desc");
  const DEFAULT_DIR = { id: "asc", vendor: "asc", project: "asc", status: "asc", owner: "asc", submitted: "desc", scheduled: "desc", amount: "desc" };
  const pickSort  = (k) => { setSortKey(k); setSortDir(DEFAULT_DIR[k] || "asc"); };
  const clickSort = (k, e) => {
    // Ignore clicks on the resize edge (right ~7px) so grabbing the column-resize
    // handle doesn't also re-sort.
    if (e) { const r = e.currentTarget.getBoundingClientRect(); if (r.right - e.clientX <= 7) return; }
    if (k === sortKey) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else pickSort(k);
  };
  const sortArrow = (k) => {
    if (sortKey !== k) return <IconChevronDn size={10} style={{ opacity: 0.22 }}/>;
    return <IconChevronDn size={10} style={sortDir === "asc" ? { transform: "rotate(180deg)" } : undefined}/>;
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  const schedOverdue = (b) => { const d = toDayIso(schedPayOf(b)); return d && d < todayIso && b.status === "scheduled"; };
  const NEXT_STATUS = {
    'pm-review':        { next: 'finance-verify',   label: 'PM Approve' },
    'finance-verify':   { next: 'approved-payment', label: 'Approve to pay' },
    'approved-payment': { next: 'scheduled',        label: 'Mark scheduled' },
    'scheduled':        { next: 'paid',             label: 'Mark paid' },
  };
  const doTransition = async (status) => {
    if (!openId || busy) return;
    if ((status === 'rejected' || status === 'disputed') && !note.trim()) {
      window.__toast?.(status === 'disputed' ? 'Add a note explaining the dispute first.' : 'Add a rejection note first.', 'error');
      return;
    }
    // PM approval requires a rating — escalate to the parent's modal.
    const current = bills.find(b => b.id === openId || b.invoice_id === openId);
    if (current?.status === 'pm-review' && status === 'finance-verify' && onPmApprove) {
      onPmApprove(current);
      return;
    }
    // Approving to pay must route to an Account Payable sheet — require a company.
    if (status === 'approved-payment' && !apCompany) {
      window.__toast?.('Choose which company to charge (Brandstorm or MotionReach) before approving to pay.', 'error');
      return;
    }
    // …and an expense category, so the AP Category column holds a valid value.
    if (status === 'approved-payment' && !apCategory) {
      window.__toast?.('Choose an expense category before approving to pay.', 'error');
      return;
    }
    // Scheduling requires a payment date — it's written to the AP Due Date column.
    if (status === 'scheduled' && !payDate) {
      window.__toast?.('Pick a payment date before marking the invoice scheduled.', 'error');
      return;
    }
    setBusy(status);
    try {
      await onStatusChange?.(openId, status, note.trim(),
        { company:  status === 'approved-payment' ? apCompany  : undefined,
          category: status === 'approved-payment' ? apCategory : undefined,
          scheduled_date: status === 'scheduled' ? payDate : undefined });
      setNote("");
    } catch (e) {
      // Toast already shown by parent
    } finally {
      setBusy(null);
    }
  };

  // Option lists for the toolbar dropdowns, derived from the live invoices.
  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
  const vendorOpts  = useMemoEx(() => [{ value: "all", label: t("All vendors") },  ...uniq(bills.map(b => b.vendor)).sort().map(v => ({ value: v, label: v }))], [bills, t]);
  const projectOpts = useMemoEx(() => [{ value: "all", label: t("All projects") }, ...uniq(bills.map(b => b.project)).sort().map(v => ({ value: v, label: v }))], [bills, t]);
  const ownerOpts   = useMemoEx(() => [{ value: "all", label: t("All owners") },   ...uniq(bills.map(b => b.owner)).sort().map(v => ({ value: v, label: v }))], [bills, t]);
  const AMOUNT_OPTS = [
    { value: "all",      label: t("Any amount") },
    { value: "lt50",     label: t("Under ฿50,000") },
    { value: "50to200",  label: t("฿50,000 – ฿200,000") },
    { value: "gt200",    label: t("Over ฿200,000") },
  ];
  const inAmount = (amt) => {
    const a = Number(amt) || 0;
    if (fAmount === "lt50")    return a < 50000;
    if (fAmount === "50to200") return a >= 50000 && a <= 200000;
    if (fAmount === "gt200")   return a > 200000;
    return true;
  };
  const anyToolbarFilter = fVendor !== "all" || fProject !== "all" || fOwner !== "all" || fAmount !== "all" || !!(subFrom || subTo || schFrom || schTo);
  const clearToolbar = () => { setFVendor("all"); setFProject("all"); setFOwner("all"); setFAmount("all"); setSubFrom(""); setSubTo(""); setSchFrom(""); setSchTo(""); };

  const filtered = useMemoEx(() => {
    let out = bills;
    if (tab === "pm")        out = out.filter(b => b.status === "pm-review");
    else if (tab === "finance")   out = out.filter(b => b.status === "finance-verify");
    else if (tab === "approved")  out = out.filter(b => b.status === "approved-payment");
    else if (tab === "scheduled") out = out.filter(b => b.status === "scheduled");
    else if (tab === "paid")      out = out.filter(b => b.status === "paid");
    else if (tab === "disputed")  out = out.filter(b => b.status === "disputed" || b.status === "rejected");
    if (fVendor !== "all")  out = out.filter(b => b.vendor === fVendor);
    if (fProject !== "all") out = out.filter(b => b.project === fProject);
    if (fOwner !== "all")   out = out.filter(b => b.owner === fOwner);
    if (fAmount !== "all")  out = out.filter(b => inAmount(netPayableOf(b)));
    // Date-range filters. A row with no date in the filtered column is excluded
    // while that range is active (it can't fall inside a range).
    if (subFrom || subTo) out = out.filter(b => { const d = toDayIso(b.submitted);   return d && (!subFrom || d >= subFrom) && (!subTo || d <= subTo); });
    if (schFrom || schTo) out = out.filter(b => { const d = toDayIso(schedPayOf(b)); return d && (!schFrom || d >= schFrom) && (!schTo || d <= schTo); });
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(b =>
        String(b.id || '').toLowerCase().includes(q) ||
        String(b.po || '').toLowerCase().includes(q) ||
        String(b.project || '').toLowerCase().includes(q) ||
        String(b.vendor || '').toLowerCase().includes(q)
      );
    }
    const baseCmp = {
      id:        (a, b) => String(a.id || "").localeCompare(String(b.id || "")),
      vendor:    (a, b) => String(a.vendor || "").localeCompare(String(b.vendor || "")),
      project:   (a, b) => String(a.project || "").localeCompare(String(b.project || "")),
      status:    (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
      owner:     (a, b) => String(a.owner || "").localeCompare(String(b.owner || "")),
      submitted: (a, b) => toDayIso(a.submitted).localeCompare(toDayIso(b.submitted)),
      scheduled: (a, b) => toDayIso(schedPayOf(a)).localeCompare(toDayIso(schedPayOf(b))),
      amount:    (a, b) => netPayableOf(a) - netPayableOf(b),
    }[sortKey];
    if (baseCmp) { const dir = sortDir === "asc" ? 1 : -1; out = [...out].sort((a, b) => dir * baseCmp(a, b)); }
    return out;
  }, [tab, bills, query, fVendor, fProject, fOwner, fAmount, subFrom, subTo, schFrom, schTo, sortKey, sortDir]);

  const open = bills.find(b => b.id === openId);
  const totals = {
    pm:        bills.filter(b => b.status === "pm-review").length,
    finance:   bills.filter(b => b.status === "finance-verify").length,
    approved:  bills.filter(b => b.status === "approved-payment").length,
    scheduled: bills.filter(b => b.status === "scheduled").length,
    paid:      bills.filter(b => b.status === "paid").length,
    disputed:  bills.filter(b => b.status === "disputed" || b.status === "rejected").length,
  };
  const sumByStatus = (st) => bills.filter(b => st.includes(b.status)).reduce((a,b) => a+netPayableOf(b), 0);

  // ⚠ FIX (2026-07-19) — stat-tile sub-lines were hardcoded ("avg 11h old",
  // "1 high-value", "Next pay run: Wed 21 May") and drifted from reality. Derive
  // them from the live invoices instead.
  const HIGH_VALUE_THB = 200000;   // matches the toolbar's "Over ฿200,000" bucket
  const avgAgeLabel = (statuses) => {
    const ages = bills
      .filter(b => statuses.includes(b.status) && b.submitted)
      .map(b => (Date.now() - new Date(b.submitted).getTime()) / 36e5)
      .filter(h => Number.isFinite(h) && h >= 0);
    if (!ages.length) return null;
    const avgH = ages.reduce((a, h) => a + h, 0) / ages.length;
    return avgH < 24 ? t("avg {n}h old", { n: Math.round(avgH) }) : t("avg {n}d old", { n: Math.round(avgH / 24) });
  };
  const pmAge = avgAgeLabel(["pm-review"]);
  const highValueCount = bills.filter(b => b.status === "finance-verify" && netPayableOf(b) >= HIGH_VALUE_THB).length;
  // Earliest upcoming scheduled payment date across invoices already scheduled.
  const nextPayIso = bills
    .filter(b => b.status === "scheduled" && b.payment_scheduled)
    .map(b => b.payment_scheduled)
    .sort()[0];
  const nextPayLabel = nextPayIso
    ? new Date(nextPayIso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })
    : null;

  if (!bills.length) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-title-group">
            <h1>{t("Billing inbox")}</h1>
            <div className="sub">{t("All supplier invoices in motion")}</div>
          </div>
        </div>
        <div style={{ padding: 48, textAlign: "center", color: "var(--text-tertiary)" }}>
          {t("No invoices in the queue yet. They'll appear here as suppliers submit them from the portal.")}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Billing inbox")}</h1>
          <div className="sub">{t("All supplier invoices in motion · Finance & PM review queue")}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm" disabled={!filtered.length}
            onClick={() => downloadCsv(`invoices-${tab}-${new Date().toISOString().slice(0,10)}.csv`, filtered, [
              { label: 'Invoice', key: 'id' },
              { label: 'Vendor', key: 'vendor' },
              { label: 'Vendor ID', key: 'vid' },
              { label: 'Project', key: 'project' },
              { label: 'Invoice No.', key: 'po' },
              { label: 'Status', key: 'status' },
              { label: 'Owner', key: 'owner' },
              { label: 'Amount (THB, gross)', get: b => netPayableOf(b) },
              { label: 'Submitted', get: b => b.submitted ? new Date(b.submitted).toLocaleDateString() : '' },
              { label: 'Scheduled payment', get: b => schedPayOf(b) ? new Date(schedPayOf(b) + 'T00:00:00').toLocaleDateString() : '' },
              { label: 'Due', get: b => b.due ? new Date(b.due).toLocaleDateString() : '' },
              { label: 'Tax invoice', get: b => b.status !== 'paid' ? '' : ((b.tax_count || 0) > 0 ? `${b.tax_count} file(s)` : 'MISSING') },
            ])}>
            <IconDownload size={14}/> {t("Export")}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => window.__app?.openModal?.("payrun")}><IconCheck size={14}/> {t("Schedule pay run")}</button>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-tile">
          <div className="label">{t("Awaiting PM review")}</div>
          <div className="val tnum" style={{color: "var(--warning)"}}>{fmtTHB(sumByStatus(["pm-review"]))}</div>
          <div className="delta">{t("{n} invoices", { n: totals.pm })}{pmAge ? ` · ${pmAge}` : ""}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Finance verify")}</div>
          <div className="val tnum" style={{color: "var(--info)"}}>{fmtTHB(sumByStatus(["finance-verify"]))}</div>
          <div className="delta">{t("{n} invoices", { n: totals.finance })}{highValueCount ? ` · ${t("{n} high-value", { n: highValueCount })}` : ""}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Approved · ready to pay")}</div>
          <div className="val tnum" style={{color: "var(--success)"}}>{fmtTHB(sumByStatus(["approved-payment","scheduled"]))}</div>
          <div className="delta">{nextPayLabel ? t("Next pay run: {date}", { date: nextPayLabel }) : t("{n} invoices", { n: totals.approved + totals.scheduled })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Paid")}</div>
          <div className="val tnum" style={{color: "var(--text-secondary)"}}>{fmtTHB(sumByStatus(["paid"]))}</div>
          <div className="delta">{t("{n} invoices", { n: totals.paid })}</div>
        </div>
      </div>

      <div className="tabs">
        {[
          ["all",       "All",            bills.length],
          ["pm",        "PM review",      totals.pm,       "warning"],
          ["finance",   "Finance verify", totals.finance,  "info"],
          ["approved",  "Approved",       totals.approved],
          ["scheduled", "Scheduled",      totals.scheduled],
          ["paid",      "Paid",           totals.paid],
          ["disputed",  "Disputed",       totals.disputed, "danger"],
        ].map(([id, lbl, n, kind]) => (
          <button key={id} className={"tab " + (tab === id ? "active" : "")} onClick={() => setTab(id)}>
            {t(lbl)}
            <span style={{
              marginLeft: 6, fontSize: 11,
              color: n > 0 && kind === "danger" ? "var(--danger)" : (n > 0 && kind === "warning") ? "var(--warning)" : "var(--text-tertiary)"
            }}>{n}</span>
          </button>
        ))}
      </div>

      <div className="toolbar">
        {anyToolbarFilter ? (
          <button className="chip active" onClick={clearToolbar}><IconX size={11}/> {t("Clear filters")}</button>
        ) : (
          <span className="chip" style={{opacity: 0.7, cursor: "default"}}><IconFilter size={12}/> {t("Filters")}</span>
        )}
        <ChipMenu label={fVendor === "all" ? t("Vendor") : fVendor}   value={fVendor}  options={vendorOpts}  onPick={setFVendor}/>
        <ChipMenu label={fProject === "all" ? t("Project") : fProject} value={fProject} options={projectOpts} onPick={setFProject}/>
        <ChipMenu label={fOwner === "all" ? t("Owner") : fOwner}       value={fOwner}   options={ownerOpts}   onPick={setFOwner}/>
        <ChipMenu label={fAmount === "all" ? t("Amount") : (AMOUNT_OPTS.find(o => o.value === fAmount) || {}).label}
          value={fAmount} options={AMOUNT_OPTS} onPick={setFAmount}/>
        <DateRangeChip label={t("Submitted")}     from={subFrom} to={subTo} onChange={(f, v) => { setSubFrom(f); setSubTo(v); }}/>
        <DateRangeChip label={t("Scheduled pay")} from={schFrom} to={schTo} onChange={(f, v) => { setSchFrom(f); setSchTo(v); }}/>
        <div className="toolbar-right">
          <div className="topbar-search" style={{maxWidth: 220, margin: 0}}>
            <IconSearch size={14} className="icon"/>
            <input placeholder={t("Search invoice, vendor, project…")} value={query} onChange={e => setQuery(e.target.value)}/>
          </div>
        </div>
      </div>

      <div style={{position: "relative", flex: 1, overflow: "hidden"}}>
        <div style={{position: "absolute", inset: 0, overflow: "auto"}}>
          {/* When the panel is open the table grows by PANEL_W and gains a
              trailing spacer column, so its real columns can be scrolled left
              out from under the floating panel. */}
          <table className="tbl" style={{minWidth: 980, width: panelOpen ? `calc(100% + ${PANEL_W})` : undefined}}>
            <colgroup>
              <col style={{width: 132}}/>
              <col style={{width: 168}}/>
              <col/>
              <col style={{width: 116}}/>
              <col style={{width: 104}}/>
              <col style={{width: 96}}/>
              <col style={{width: 108}}/>
              <col style={{width: 52}}/>
              <col style={{width: 96}}/>
              {panelOpen && <col style={{width: PANEL_W}}/>}
            </colgroup>
            <thead>
              <tr>
                {[
                  ["id",        t("Invoice"),               false],
                  ["vendor",    t("Vendor"),                false],
                  ["project",   t("Project · Invoice no."), false],
                  ["status",    t("Status"),                false],
                  ["owner",     t("Owner"),                 false],
                  ["submitted", t("Submitted"),             false],
                  ["scheduled", t("Scheduled pay"),         false],
                  [null,        t("Age"),                   false],
                  ["amount",    t("Amount"),                true],
                ].map(([k, label, right]) => (
                  k ? (
                    <th key={k} className={right ? "right" : undefined}
                      onClick={(e) => clickSort(k, e)}
                      aria-sort={sortKey === k ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                      title={t("Sort by {c}", { c: label })}
                      style={{ cursor: "pointer" }}>
                      <span className="sort" style={right ? { justifyContent: "flex-end" } : undefined}>{label} {sortArrow(k)}</span>
                    </th>
                  ) : (
                    <th key="age">{label}</th>
                  )
                ))}
                {panelOpen && <th aria-hidden="true" style={{background: "transparent"}}/>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id}
                  className={panelOpen && openId === b.id ? "selected" : ""}
                  onClick={() => openRow(b.id)}
                  style={{cursor: "pointer"}}>
                  <td>
                    <div style={{display:"flex", alignItems:"center", gap: 8}}>
                      <span className="mono" style={{color: "var(--text-primary)", fontSize: 12}}>{b.id}</span>
                      {b.flag === "high-value" && <span className="pill pill-warning" style={{height: 16, padding: "0 5px", fontSize: 9}}>HIGH</span>}
                      {b.flag === "supplier-replied" && <span className="pill pill-info" style={{height: 16, padding: "0 5px", fontSize: 9}}>REPLY</span>}
                      {/* ⚠ NEW (2026-07-27) — paid invoices show whether the supplier's tax invoice has arrived */}
                      {b.status === "paid" && ((b.tax_count || 0) > 0
                        ? <span className="pill pill-success" style={{height: 16, padding: "0 5px", fontSize: 9}} title={t("Tax invoice received")}>TAX</span>
                        : <span className="pill pill-warning" style={{height: 16, padding: "0 5px", fontSize: 9}} title={t("Tax invoice not yet received")}>NO TAX</span>)}
                    </div>
                  </td>
                  <td>
                    <div style={{display:"flex", alignItems:"center", gap: 8}}>
                      <Avatar name={b.vendor} size="sm"/>
                      <span className="ellipsis">{b.vendor}</span>
                    </div>
                  </td>
                  <td className="muted ellipsis">{b.project} · <span className="mono" style={{fontSize: 11}}>{b.po}</span></td>
                  <td><StatusPill meta={BILLING_STATUS_META[b.status]}/></td>
                  <td>
                    <div style={{display:"flex", alignItems:"center", gap: 6}}>
                      <Avatar name={b.owner} size="sm"/>
                      <span className="muted" style={{fontSize: 12}}>{b.owner}</span>
                    </div>
                  </td>
                  <td className="mono muted" style={{fontSize: 12}}>{fmtDayCell(b.submitted) || "—"}</td>
                  <td className="mono" style={{
                    fontSize: 12,
                    color: schedOverdue(b) ? "var(--warning)" : (schedPayOf(b) ? "var(--text-secondary)" : "var(--text-tertiary)")
                  }} title={schedOverdue(b) ? t("Payment date has passed") : undefined}>{fmtDayCell(schedPayOf(b)) || "—"}</td>
                  <td>
                    <span className="mono" style={{
                      fontSize: 12,
                      color: b.age && b.age.includes("d") && parseInt(b.age) > 3 ? "var(--warning)" : "var(--text-tertiary)"
                    }}>{b.age}</span>
                  </td>
                  <td className="num right tnum">{fmtTHB(netPayableOf(b))}</td>
                  {panelOpen && <td aria-hidden="true"/>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {open && panelOpen && (
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0,
            width: PANEL_W, background: "var(--bg-secondary)",
            borderLeft: "1px solid var(--border-subtle)", boxShadow: "var(--elevation-3)",
            zIndex: 20, display: "flex", flexDirection: "column",
          }}>
            <button className="icon-btn" onClick={() => setPanelOpen(false)}
              title={t("Hide panel (Esc)")} aria-label={t("Hide panel")}
              style={{position: "absolute", top: 12, right: 12, zIndex: 2}}>
              <IconX size={16}/>
            </button>
            <div style={{overflowY: "auto", padding: 20, paddingTop: 44, flex: 1}}>
              <div style={{display:"flex", flexDirection:"column", gap: 14}}>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start"}}>
                <div>
                  <div className="mono" style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em"}}>{t("Invoice")}</div>
                  <div style={{fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 500, marginTop: 4}}>{open.id}</div>
                </div>
                <StatusPill meta={BILLING_STATUS_META[open.status]}/>
              </div>

              <div style={{
                background: "var(--bg-primary)", padding: 16,
                borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)",
              }}>
                <div className="muted" style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em"}}>{t("Amount due")}</div>
                <div style={{fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", marginTop: 4}}>
                  {fmtTHB(netPayableOf(open))}
                </div>
                <div style={{marginTop: 4, fontSize: 12, color: "var(--text-tertiary)"}}>
                  {t("Inclusive of 7% VAT · WHT 3% withheld")}
                </div>
              </div>

              <dl className="kv" style={{gridTemplateColumns: "110px 1fr"}}>
                <dt>{t("Vendor")}</dt><dd style={{display:"flex", alignItems:"center", gap:8}}><Avatar name={open.vendor} size="sm"/>{open.vendor}</dd>
                <dt>{t("Vendor ID")}</dt><dd className="mono">{open.vid}</dd>
                <dt>{t("Project")}</dt><dd>{open.project}</dd>
                <dt>{t("Invoice no.")}</dt><dd className="mono">{open.po}</dd>
                <dt>{t("Submitted")}</dt><dd className="mono">{open.submitted ? new Date(open.submitted).toLocaleDateString() : '—'}</dd>
                <dt>{t("Due date")}</dt><dd className="mono">{open.due ? new Date(open.due).toLocaleDateString() : '—'}</dd>
              </dl>

              <div>
                <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10}}>{t("Submission checklist")}</div>
                <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                  {/* ⚠ Real per-invoice checks computed from the submission (was hardcoded all-green) */}
                  {[
                    [t("Invoice number provided"), !!(open.po && String(open.po).trim())],
                    [t("Invoice document attached"), !!open.has_pdf],
                    [t("Proof of performance attached"), (open.pop_count || 0) > 0],
                    [t("Amount is positive"), Number(open.amount) > 0],
                    [t("Invoice date provided"), !!open.invoice_date],
                    [t("VAT recorded"), open.vat_thb !== undefined && open.vat_thb !== null && open.vat_thb !== ''],
                    // ⚠ NEW (2026-07-27) — post-payment: has the supplier sent the tax invoice?
                    ...(open.status === 'paid' ? [[t("Tax invoice received"), (open.tax_count || 0) > 0]] : []),
                  ].map(([t, ok], i) => (
                    <div key={i} style={{display:"flex", alignItems:"center", gap: 10, fontSize: 13}}>
                      <span style={{
                        width: 18, height: 18, borderRadius: "50%",
                        background: ok ? "color-mix(in oklab, var(--success) 22%, transparent)" : "color-mix(in oklab, var(--warning) 22%, transparent)",
                        color: ok ? "var(--success)" : "var(--warning)",
                        display:"inline-flex", alignItems:"center", justifyContent:"center"
                      }}>{ok ? <IconCheck size={11}/> : "!"}</span>
                      <span style={{color: ok ? "var(--text-secondary)" : "var(--text-primary)"}}>{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ⚠ MODIFIED FROM PORT — real attachment from the Invoices sheet,
                  plus a wired download button. */}
              <InvoiceAttachment invoice={open}/>

              <div className="hr"></div>

              <div className="field">
                <label className="field-label">{t("Internal note (required to reject or dispute)")}</label>
                <textarea className="input" placeholder={t("Add context for the next reviewer, or explain the dispute…")}
                  value={note} onChange={(e) => setNote(e.target.value)}/>
              </div>

              {/* ⚠ NEW (2026-06-05) — AP routing. Shown at the finance-verify step,
                  to whoever can approve to pay. On approval the invoice is posted
                  to this company's Account Payable sheet. */}
              {open && open.status === 'finance-verify' && canDo('approved-payment') && (
                <div className="field">
                  <label className="field-label">{t("Charge to company · Account Payable")}</label>
                  <select className="input" value={apCompany} onChange={(e) => setApCompany(e.target.value)}>
                    <option value="">{t("Select company…")}</option>
                    <option value="brandstorm">Brandstorm</option>
                    <option value="motionreach">MotionReach</option>
                  </select>
                  <div className="muted" style={{fontSize: 11, marginTop: 6}}>
                    {apCompany
                      ? t("On approval this invoice is written to the {company} Account Payable sheet (net of WHT, status “awaiting payment”).", { company: apCompany === 'brandstorm' ? 'Brandstorm' : 'MotionReach' })
                      : t("Required — picks which AP sheet this approved invoice is posted to.")}
                  </div>
                </div>
              )}

              {/* ⚠ NEW (2026-06-08) — Expense category. Finance picks the Expense
                  Main Category the invoice is booked under; the options come live
                  from the chosen company's finance-sheet category list. */}
              {open && open.status === 'finance-verify' && canDo('approved-payment') && (
                <div className="field">
                  <label className="field-label">{t("Expense category · Account Payable")}</label>
                  <select className="input" value={apCategory} disabled={!apCompany || catLoading}
                    onChange={(e) => setApCategory(e.target.value)}>
                    <option value="">
                      {!apCompany ? t("Select a company first…")
                        : catLoading ? t("Loading categories…")
                        : t("Select expense category…")}
                    </option>
                    {catOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="muted" style={{fontSize: 11, marginTop: 6}}>
                    {t("Required — the expense category this invoice is booked under in the Account Payable sheet.")}
                  </div>
                </div>
              )}

              {/* ⚠ NEW (2026-06-17) — Payment date. Finance picks when the invoice
                  will be paid; on "Mark scheduled" it's written to the AP sheet's
                  Due Date column for the matching row. */}
              {open && open.status === 'approved-payment' && canDo('scheduled') && (
                <div className="field">
                  <label className="field-label">{t("Payment date")}</label>
                  {/* Native date picker — clicking anywhere on the field pops the
                      calendar (showPicker), so it's a click-to-pick, not type-only. */}
                  <input type="date" className="input mono" value={payDate}
                    style={{cursor: 'pointer'}}
                    onChange={(e) => setPayDate(e.target.value)}
                    onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) {} }}/>
                  <div className="muted" style={{fontSize: 11, marginTop: 6}}>
                    {t("Written to the Account Payable sheet's Due Date column when you mark this scheduled.")}
                  </div>
                </div>
              )}

              <div style={{display:"flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", alignItems: "center"}}>
                <div style={{display:"flex", gap: 8, marginRight: 'auto'}}>
                  {open && !['paid','rejected','disputed'].includes(open.status) && (role === 'admin' || role === 'pm' || role === 'finance') && (
                    <button className="btn btn-ghost btn-sm"
                      disabled={!!busy}
                      onClick={() => setEditInvoice(open)}>
                      {t('Edit details')}
                    </button>
                  )}
                  {/* ⚠ NEW — admin-only hard delete (test / mistaken invoices). Removes
                      the invoice, its Drive files and any Account Payable row. */}
                  {open && canDelete && (
                    <button className="btn btn-ghost btn-sm" style={{color: "var(--danger)"}}
                      disabled={!!busy}
                      onClick={() => onDelete?.(open)}>
                      {t('Delete')}
                    </button>
                  )}
                </div>
                <div style={{display:"flex", gap: 8}}>
                  {open && !['paid','rejected','disputed'].includes(open.status) && (
                    <button className="btn btn-ghost btn-sm" style={{color: "var(--warning)"}}
                      disabled={!!busy}
                      onClick={() => doTransition('disputed')}>
                      {busy === 'disputed' ? t('Flagging…') : t('Dispute')}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{color: "var(--danger)"}}
                    disabled={!!busy || !open || ['paid','rejected'].includes(open.status)}
                    onClick={() => doTransition('rejected')}>
                    <IconX size={14}/> {busy === 'rejected' ? t('Rejecting…') : t('Reject')}
                  </button>
                  {open && NEXT_STATUS[open.status] && canDo(NEXT_STATUS[open.status].next) && (
                    <button className="btn btn-primary btn-sm"
                      disabled={!!busy}
                      onClick={() => doTransition(NEXT_STATUS[open.status].next)}>
                      <IconCheck size={14}/> {busy === NEXT_STATUS[open.status].next
                        ? t('Working…')
                        : t(NEXT_STATUS[open.status].label)}
                    </button>
                  )}
                  {open && NEXT_STATUS[open.status] && !canDo(NEXT_STATUS[open.status].next) && (
                    <span className="muted" style={{fontSize: 12, alignSelf: "center"}}>
                      {NEXT_STATUS[open.status].next === 'finance-verify' ? t('Awaiting PM approval') : t('Awaiting Finance')}
                    </span>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <EditInvoiceModal
        open={!!editInvoice}
        invoice={editInvoice}
        mode="staff"
        onClose={() => setEditInvoice(null)}
        onSaved={() => { setEditInvoice(null); onRefresh?.(); }}
      />
    </div>
  );
}

/* ============================================================
   SCREEN — SUPPLIER PORTAL: COMPANY PROFILE
   ============================================================ */
// ⚠ MODIFIED FROM PORT — wires the company profile to live vendor data.
export function ScreenCompanyProfile({ vendor, invoices = [] }) {
  // ⚠ MODIFIED FROM PORT (2026-05-31) — removed the duplicate mock banking block
  // and all fabricated sections (commercial terms / tags / performance / named
  // contacts). Everything below is the supplier's real record; the performance
  // summary computes from this vendor's live invoices.
  const t = useT();
  const [v, setV] = useStateEx(vendor || {});
  React.useEffect(() => { setV(vendor || {}); }, [vendor]);
  const [editing, setEditing] = useStateEx(false);
  const [form, setForm] = useStateEx({});
  const [savingP, setSavingP] = useStateEx(false);
  const startEdit = () => {
    setForm({ contact: v.contact || '', phone: v.phone || '', line_id: v.line_id || '', address: v.address || '', city: v.city || '', website: v.website || '' });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSavingP(true);
    try {
      await api.updatePortalProfile(form);
      setV(prev => ({ ...prev, ...form }));
      setEditing(false);
      window.__toast?.('Details updated', 'success');
    } catch (e) { window.__toast?.(e.message, 'error'); }
    finally { setSavingP(false); }
  };
  const vid = v.vendor_id || v.id;
  const myInv = (invoices || []).filter(i => (i.vendor_id || i.vid) === vid);
  const thisYear = new Date().getFullYear();
  const spendYtd = myInv
    .filter(i => i.status === 'paid' && i.paid_at && new Date(i.paid_at).getFullYear() === thisYear)
    .reduce((a, b) => a + netPayableOf(b), 0);
  const projectCount = new Set(myInv.filter(i => i.po_number || i.po).map(i => i.po_number || i.po)).size;
  const paidInv = myInv.filter(i => i.status === 'paid' && i.paid_at);
  const onTime = paidInv.filter(i => !i.due_at || new Date(i.paid_at) <= new Date(i.due_at));
  const onTimePct = paidInv.length ? Math.round(onTime.length / paidInv.length * 100) : null;
  const creditLimit = v.credit_limit || v.creditLimit;
  const activeSince = v.created_at ? new Date(v.created_at) : null;
  const statusMeta = v.status === 'active'    ? { pill: 'pill-success', label: 'Active supplier' }
                   : v.status === 'blacklist' ? { pill: 'pill-danger',  label: 'Suspended' }
                   : v.status === 'review'    ? { pill: 'pill-warning', label: 'In review' }
                   : { pill: 'pill-neutral', label: v.status || '—' };  // label t()'d at render
  const pm = v.assigned_pm_email;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Company profile")}</h1>
          <div className="sub">{v.name ? t("Details on file for {name}", { name: v.name }) : t('Your details on file with MotionReach')}</div>
        </div>
        <div className="page-actions">
          {!editing && <button className="btn btn-secondary btn-sm" onClick={startEdit}>{t("Edit contact details")}</button>}
        </div>
      </div>

      <div style={{padding: "0 24px 12px"}}>
        <div style={{
          padding: 16,
          background: "var(--bg-secondary)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-md)",
          borderLeft: "3px solid var(--brand-cyan)",
          display: "flex", gap: 14, alignItems: "center",
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "color-mix(in oklab, var(--info) 18%, transparent)",
            color: "var(--info)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}><IconShield size={18}/></span>
          <div style={{flex: 1}}>
            <div style={{fontSize: 13, fontWeight: 400}}>{t("You can update your own contact details.")}</div>
            <div className="muted" style={{fontSize: 12, marginTop: 2}}>
              {t("Your legal name, tax ID, and bank details are locked for compliance — email your MotionReach contact to change those, and they'll route it through Procurement & Finance.")}
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <div style={{padding: "0 24px 12px"}}>
          <div className="section">
            <div className="section-head"><h3>{t("Edit your contact details")}</h3></div>
            <div className="section-body">
              <div className="form-grid">
                <div className="field">
                  <label className="field-label">{t("Primary contact")}</label>
                  <input className="input" value={form.contact} onChange={e => setForm(f => ({...f, contact: e.target.value}))} disabled={savingP}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Phone")}</label>
                  <input className="input" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} disabled={savingP}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("LINE ID")}</label>
                  <input className="input mono" value={form.line_id} onChange={e => setForm(f => ({...f, line_id: e.target.value}))} disabled={savingP}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Website")}</label>
                  <input className="input mono" value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} disabled={savingP}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("City")}</label>
                  <input className="input" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} disabled={savingP}/>
                </div>
                <div className="field full">
                  <label className="field-label">{t("Address")}</label>
                  <textarea className="input" rows={2} value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} disabled={savingP}/>
                </div>
              </div>
              <div className="muted" style={{fontSize: 12, marginTop: 10, lineHeight: 1.5}}>
                {t("Company name, tax ID, and bank details can't be self-edited — email your MotionReach contact to change those.")}
              </div>
            </div>
            <div style={{padding: "12px 18px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8}}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} disabled={savingP}>{t("Cancel")}</button>
              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={savingP}>{savingP ? t('Saving…') : t('Save changes')}</button>
            </div>
          </div>
        </div>
      )}

      <div className="detail">
        <div className="detail-main">
          <div className="section">
            <div className="section-head">
              <h3><IconBuilding size={12} style={{display:"inline-block", verticalAlign:"middle", marginRight: 6}}/> {t("Legal entity")}</h3>
            </div>
            <div className="section-body">
              <dl className="kv">
                <dt>{t("Company name")}</dt><dd>{v.name || '—'}</dd>
                <dt>{t("Vendor ID")}</dt><dd className="mono">{vid || '—'}</dd>
                <dt>{t("Tax ID / VAT")}</dt><dd><span className="mono">{padThaiId(v.tax_id || v.taxId) || '—'}</span></dd>
                <dt>{t("Contact")}</dt><dd>{v.contact || '—'}</dd>
                <dt>{t("Contact email")}</dt><dd className="mono" style={{fontSize: 12}}>{v.email || '—'}</dd>
                <dt>{t("Phone")}</dt><dd className="mono">{v.phone || '—'}</dd>
                <dt>{t("LINE")}</dt><dd className="mono">{v.line_id || '—'}</dd>
                <dt>{t("Address")}</dt><dd style={{whiteSpace:"pre-wrap"}}>{v.address || '—'}</dd>
                <dt>{t("City")}</dt><dd>{v.city || '—'}</dd>
                <dt>{t("Country")}</dt><dd>{v.country || '—'}</dd>
                <dt>{t("Website")}</dt><dd className="mono" style={{fontSize: 12}}>{v.website || '—'}</dd>
              </dl>
            </div>
          </div>

          <div className="section">
            <div className="section-head">
              <h3><IconBank size={12} style={{display:"inline-block", verticalAlign:"middle", marginRight: 6}}/> {t("Banking")}</h3>
            </div>
            <div className="section-body">
              <dl className="kv">
                <dt>{t("Bank")}</dt><dd>{v.bank_name || '—'}</dd>
                <dt>{t("Account number")}</dt><dd className="mono">{v.account_number || '—'}</dd>
                <dt>{t("Account name")}</dt><dd>{v.account_name || '—'}</dd>
              </dl>
            </div>
          </div>

          <div className="section">
            <div className="section-head">
              <h3><IconReceipt size={12} style={{display:"inline-block", verticalAlign:"middle", marginRight: 6}}/> {t("Commercial terms")}</h3>
            </div>
            <div className="section-body">
              <dl className="kv">
                <dt>{t("Credit terms")}</dt><dd className="mono">{v.terms || '—'}</dd>
                <dt>{t("Credit limit")}</dt><dd>{creditLimit ? fmtTHB(Number(creditLimit)) : '—'}</dd>
                <dt>{t("Currency")}</dt><dd>THB</dd>
                <dt>{t("Category")}</dt><dd>{v.cat ? <CategoryTag id={v.cat}/> : '—'}</dd>
                <dt>{t("Active since")}</dt><dd>{activeSince && !Number.isNaN(activeSince.getTime()) ? activeSince.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—'}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="detail-aside">
          <div className="section">
            <div className="section-head"><h3>{t("Performance summary")}</h3></div>
            <div className="section-body" style={{display:"flex", flexDirection:"column", gap: 14}}>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t("Paid to you ({year})", { year: thisYear })}</div>
                <div style={{fontSize: 22, fontWeight: 600, fontVariantNumeric: "tabular-nums", marginTop: 2}}>{fmtTHB(spendYtd)}</div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t("Projects (by PO)")}</div>
                <div style={{fontSize: 18, fontWeight: 500, fontVariantNumeric: "tabular-nums", marginTop: 2}}>{projectCount}</div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t("On-time delivery")}</div>
                <div style={{fontSize: 18, fontWeight: 500, fontVariantNumeric: "tabular-nums", marginTop: 2}}>
                  {onTimePct === null ? '—' : `${onTimePct}%`}
                  {paidInv.length > 0 && <span className="muted" style={{fontSize: 12}}> · {t("{n} paid", { n: paidInv.length })}</span>}
                </div>
              </div>
              <div className="hr" style={{margin: 0}}></div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t("Status")}</div>
                <div style={{marginTop: 6}}>
                  <span className={"pill " + statusMeta.pill}><span className="dot"></span> {t(statusMeta.label)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="section-head"><h3>{t("Need a change?")}</h3></div>
            <div className="section-body" style={{display: "flex", flexDirection: "column", gap: 12}}>
              <div className="muted" style={{fontSize: 12, lineHeight: 1.6}}>
                {t("Reach out to update your details or ask about an invoice.")}
              </div>
              {pm && (
                <div style={{display:"flex", gap: 10, alignItems: "center"}}>
                  <Avatar name={pm.split('@')[0]} size="sm"/>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontSize: 13}}>{t("Your project manager")}</div>
                    <div className="mono muted ellipsis" style={{fontSize: 11}}>{pm}</div>
                  </div>
                </div>
              )}
              <div style={{display:"flex", gap: 10, alignItems: "center"}}>
                <span style={{width: 28, height: 28, borderRadius: "50%", background: "var(--interactive-secondary)", color: "var(--text-secondary)", display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink: 0}}><IconBank size={13}/></span>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{fontSize: 13}}>{t("Accounts payable")}</div>
                  <div className="mono muted ellipsis" style={{fontSize: 11}}>finance@motionreach.co.th</div>
                </div>
              </div>
              <div style={{display:"flex", gap: 10, alignItems: "center"}}>
                <span style={{width: 28, height: 28, borderRadius: "50%", background: "var(--interactive-secondary)", color: "var(--text-secondary)", display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink: 0}}><IconBuilding size={13}/></span>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{fontSize: 13}}>{t("Procurement")}</div>
                  <div className="mono muted ellipsis" style={{fontSize: 11}}>vendors@motionreach.co.th</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ⚠ NEW (2026-06-10) — supplier self-service banking. Banking moved out of the
   internal registration form to the supplier side: the supplier enters it here,
   on the document screen, at the same time they upload their KYC documents.
   Saves to their own vendor record via api_updatePortalProfile. */
function BankingSection({ vendor, onSaved }) {
  const t = useT();
  const isIndividual = vendor && vendor.vendor_type === 'individual';
  const [form, setForm] = useStateEx({
    bank_name:      vendor?.bank_name      || '',
    account_number: vendor?.account_number || '',
    account_name:   vendor?.account_name   || '',
  });
  const [busy, setBusy] = useStateEx(false);
  const [saved, setSaved] = useStateEx(false);
  // The vendor record loads async in the parent — re-sync the form once it
  // arrives (or when switching vendors), but don't clobber edits in progress.
  useEffectEx(() => {
    setForm({
      bank_name:      vendor?.bank_name      || '',
      account_number: vendor?.account_number || '',
      account_name:   vendor?.account_name   || '',
    });
    setSaved(false);
  }, [vendor?.vendor_id]);

  const set = (k) => (e) => { const val = e.target.value; setForm(f => ({ ...f, [k]: val })); setSaved(false); };
  const complete = form.bank_name.trim() && form.account_number.trim() && form.account_name.trim();
  const onFile = vendor?.bank_name && vendor?.account_number && vendor?.account_name;

  // Digit count the chosen bank prints on its passbook (null = not checked).
  const expectDigits = bankAccountDigits(form.bank_name);
  const digits = form.account_number.replace(/\D/g, '');

  const save = async () => {
    if (!complete) { window.__toast?.(t('Fill in bank, account number, and account name.'), 'error'); return; }
    if (expectDigits && digits.length !== expectDigits) {
      window.__toast?.(t('{bank} account numbers have {n} digits — you entered {m}.',
        { bank: t(form.bank_name), n: expectDigits, m: digits.length }), 'error');
      return;
    }
    setBusy(true);
    try {
      await api.updatePortalProfile({
        bank_name:      form.bank_name.trim(),
        account_number: form.account_number.trim(),
        account_name:   form.account_name.trim(),
      });
      setSaved(true);
      window.__toast?.(t('Banking details saved'), 'success');
      onSaved?.();   // refresh the parent's vendor record so "on file" sticks
    } catch (e) { window.__toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const req = <span style={{ color: 'var(--danger)' }}>*</span>;
  return (
    <div className="section" style={{ marginBottom: 16 }}>
      <div className="section-head"><h3>{t("Banking")}</h3>
        <span className="muted" style={{ fontSize: 12 }}>{t("Used for invoice settlement.")}</span>
      </div>
      <div className="section-body">
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
          {isIndividual
            ? t("Enter the bank account MotionReach should pay your invoices into. The account name must match your registered name.")
            : t("Enter the bank account MotionReach should pay your invoices into. The account name must match your registered company.")}
        </div>
        <div className="form-grid">
          <div className="field">
            <label className="field-label">{t("Bank")} {req}</label>
            <div className="select">
              <select value={form.bank_name} onChange={set('bank_name')} disabled={busy}>
                <option value="">{t("— Select bank —")}</option>
                {BANKS.map(b => <option key={b} value={b}>{t(b)}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field-label">{t("Account number")} {req}</label>
            <input className="input mono" inputMode="numeric" value={form.account_number} onChange={set('account_number')} disabled={busy}
              placeholder={bankAccountPlaceholder(form.bank_name)}
              aria-invalid={(expectDigits && digits.length > 0 && digits.length !== expectDigits) || undefined}
              style={(expectDigits && digits.length > 0 && digits.length !== expectDigits) ? { borderColor: 'var(--danger)' } : undefined}/>
            <span className="field-help">
              {expectDigits === 12
                ? t("Government Savings Bank accounts are 12 digits (dashes optional).")
                : expectDigits === 10
                  ? t("10 digits (dashes optional).")
                  : t("As printed on your bank book.")}
            </span>
          </div>
          <div className="field full">
            <label className="field-label">{t("Account name")} ({t("must match")} {isIndividual ? t('the individual') : t('legal entity')}) {req}</label>
            <input className="input" value={form.account_name} onChange={set('account_name')} disabled={busy}
              placeholder={isIndividual ? 'Somchai Jaidee' : 'Tinker Post Bangkok Co., Ltd.'}/>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
          <div className="muted" style={{ fontSize: 12 }}>
            {onFile ? t("Banking on file — update it any time.") : t("Required before your invoices can be settled.")}
          </div>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={busy || !complete}>
            {busy ? t('Saving…') : saved ? t('Saved ✓') : t('Save banking details')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ⚠ NEW (2026-05-31) — vendor self-service KYC re-upload + list. */
function KycDocsSection({ vendor }) {
  const t = useT();
  const [docs, setDocs] = useStateEx(null);
  const [loading, setLoading] = useStateEx(true);
  const [err, setErr] = useStateEx(null);
  const [files, setFiles] = useStateEx({});   // slot key → File (multi-upload at once)
  const [busy, setBusy] = useStateEx(false);

  const load = () => {
    setLoading(true);
    api.listMyCompliance().then(d => { setDocs(d); setErr(null); }).catch(e => setErr(e.message)).finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, []);

  const DOC_LABEL = { 'business-registration': 'Business registration', 'tax_cert': 'VAT certificate', 'national_id': 'National ID copy', 'book_bank': 'Bank book', 'rate_card': 'Rate card', 'company_profile': 'Company profile', 'insurance': 'Insurance', 'other': 'Other' };
  const STATUS_PILL = { valid: 'pill-success', 'expiring-30d': 'pill-warning', expired: 'pill-danger', missing: 'pill-neutral', pending: 'pill-neutral' };

  // Supplier-type-aware required docs + which upload types are offered.
  const isIndividual = vendor && vendor.vendor_type === 'individual';
  const REQUIRED = isIndividual
    ? [['national_id', 'National ID copy'], ['book_bank', 'Bank book']]
    : [['business-registration', 'Business registration'], ['book_bank', 'Bank book']];
  // Upload slots shown all at once (mirrors the supplier-creation KYC grid):
  // required docs first, then optional. [key, label, required].
  const SLOTS = isIndividual
    ? [['national_id', 'National ID copy', true], ['book_bank', 'Bank book copy', true],
       ['other', 'Other document', false]]
    : [['business-registration', 'Business registration', true], ['book_bank', 'Bank book copy', true],
       ['tax_cert', 'VAT certificate', false], ['rate_card', 'Rate card', false],
       ['company_profile', 'Company profile', false]];
  const hasType = (t) => (docs || []).some(d => d.doc_type === t && d.has_file !== false);
  const setSlot = (key) => (e) => setFiles(f => ({ ...f, [key]: e.target.files?.[0] || null }));
  const selectedCount = Object.values(files).filter(Boolean).length;
  // Required docs still missing (not already on file AND not attached now) block
  // submit — this is what "force the supplier to upload them" means in practice.
  const missingRequired = SLOTS.filter(([key, , req]) => req && !hasType(key) && !files[key]);
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

  const upload = async () => {
    const picked = SLOTS.map(([key]) => [key, files[key]]).filter(([, f]) => f);
    if (!picked.length) { window.__toast?.('Choose at least one document to upload', 'error'); return; }
    if (missingRequired.length) {
      window.__toast?.('Attach all required documents first: ' + missingRequired.map(([, l]) => l).join(', '), 'error');
      return;
    }
    for (const [, f] of picked) {
      if (f.size > 20 * 1024 * 1024) { window.__toast?.(`${f.name} is larger than 20MB`, 'error'); return; }
    }
    setBusy(true);
    try {
      for (const [key, f0] of picked) {
        const f = await compressImage(f0);   // phone photos shrink ~10×; PDFs pass through
        const base64 = await fileToBase64(f);
        await api.uploadKycDoc(key, base64, f.name);
      }
      window.__toast?.(picked.length === 1 ? 'Document uploaded' : `${picked.length} documents uploaded`, 'success');
      setFiles({});
      load();
    } catch (e) { window.__toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="section" style={{marginBottom: 16}}>
      <div className="section-head"><h3>{t("KYC & compliance documents")}</h3>
        <span className="muted" style={{fontSize: 12}}>{t(isIndividual ? 'Individual' : 'Company')} {t('account')}</span>
      </div>
      <div className="section-body">
        <div style={{marginBottom: 14}}>
          <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 8}}>{t("Required documents")}</div>
          <div style={{display: "flex", flexDirection: "column", gap: 6}}>
            {REQUIRED.map(([dt, label]) => {
              const ok = hasType(dt);
              return (
                <div key={dt} style={{display: "flex", alignItems: "center", gap: 10, fontSize: 13}}>
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%",
                    background: ok ? "color-mix(in oklab, var(--success) 22%, transparent)" : "color-mix(in oklab, var(--warning) 22%, transparent)",
                    color: ok ? "var(--success)" : "var(--warning)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11
                  }}>{ok ? '✓' : '!'}</span>
                  <span style={{color: ok ? "var(--text-secondary)" : "var(--text-primary)"}}>{t(label)}</span>
                  <span className="muted" style={{fontSize: 11}}>{ok ? t('on file') : t('needed')}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 8}}>
          {t("Upload documents")} <span className="muted" style={{textTransform: 'none', letterSpacing: 0}}>{t("(PDF or image · max 20 MB each)")}</span>
        </div>
        <div className="form-grid">
          {SLOTS.map(([key, label, req]) => {
            const onFile = hasType(key);
            return (
              <div className="field" key={key}>
                <label className="field-label">
                  {t(label)} {req
                    ? <span style={{color: 'var(--danger)'}}>*</span>
                    : <span className="muted">({t('optional')})</span>}
                  {onFile && <span className="muted" style={{marginLeft: 6, fontSize: 11}}>· {t('on file')}</span>}
                </label>
                <input type="file" accept="application/pdf,image/*" onChange={setSlot(key)} disabled={busy}
                  style={{display: 'block', padding: 6, fontSize: 12}}/>
                {files[key] && (
                  <div style={{marginTop: 4, fontSize: 11, color: 'var(--text-tertiary)'}}>
                    {files[key].name} · {(files[key].size / 1024).toFixed(0)} KB
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12, flexWrap: 'wrap'}}>
          <div className="muted" style={{fontSize: 12}}>
            {missingRequired.length
              ? t('Still needed: {list}', { list: missingRequired.map(([, l]) => t(l)).join(', ') })
              : t("Uploads are added to your MotionReach vendor file and reviewed by Procurement.")}
          </div>
          <button className="btn btn-primary btn-sm" onClick={upload}
            disabled={busy || !selectedCount || missingRequired.length > 0}>
            {busy ? t('Uploading…')
              : selectedCount > 1 ? t('Upload {n} documents', { n: selectedCount })
              : t('Upload document')}
          </button>
        </div>
      </div>
      {loading ? (
        <div style={{padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13}}>{t("Loading documents…")}</div>
      ) : err ? (
        <div style={{padding: 20, color: 'var(--danger)', fontSize: 13}}>{err}</div>
      ) : docs && docs.length ? (
        <table className="tbl" style={{tableLayout: 'auto'}}>
          <thead><tr><th>{t('Document')}</th><th>{t('Status')}</th><th>{t('Issued')}</th><th>{t('Expires')}</th></tr></thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.compliance_id}>
                <td>{t(DOC_LABEL[d.doc_type] || d.doc_type)}</td>
                <td><span className={"pill " + (STATUS_PILL[d.status] || 'pill-neutral')} style={{fontSize: 11, textTransform: 'capitalize'}}>{String(d.status || '').replace('-30d', ' (≤30d)')}</span></td>
                <td className="muted" style={{fontSize: 12}}>{fmt(d.issued_at)}</td>
                <td className="muted" style={{fontSize: 12}}>{d.expires_at ? fmt(d.expires_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{padding: 20, color: 'var(--text-tertiary)', fontSize: 13}}>{t('No compliance documents on file yet.')}</div>
      )}
    </div>
  );
}

export function ScreenDocuments({ invoices = [], vendor, onProfileSaved }) {
  const t = useT();
  const invoicesWithPdf = invoices.filter(i => i.invoice_pdf_id);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Documents")}</h1>
          <div className="sub">
            {t("Invoice & compliance documents on file")}
          </div>
        </div>
      </div>

      <div style={{padding: "8px 24px 24px"}}>
        <BankingSection vendor={vendor} onSaved={onProfileSaved}/>
        <KycDocsSection vendor={vendor}/>

        {invoicesWithPdf.length === 0 ? (
          <div style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
            {t("No documents submitted yet. Invoice PDFs you upload via Submit invoice will appear here.")}
          </div>
        ) : (
          <div className="section">
            <div className="section-head"><h3>{t("Submitted invoice documents")}</h3></div>
            <div className="section-body">
              {invoicesWithPdf.map(inv => (
                <div className="file-row" key={inv.invoice_id || inv.id}>
                  <div className="file-icon">pdf</div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div className="file-name">{inv.invoice_id || inv.id}</div>
                    <div className="file-meta">
                      {inv.project || '—'} · {t("submitted")} {inv.submitted ? new Date(inv.submitted).toLocaleDateString() : '—'}
                    </div>
                  </div>
                  <span className="pill pill-neutral">{t(inv.status || '—')}</span>
                </div>
              ))}
              <div style={{padding: "12px 18px", fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5}}>
                {t("Download is available to MotionReach staff. To re-send a copy, email your MotionReach contact.")}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceAttachment({ invoice }) {
  const t = useT();
  const [busyKey, setBusyKey] = useStateEx(null);
  const invoiceId = invoice?.invoice_id || invoice?.id;

  const popAttachments = Array.isArray(invoice?.pop_attachments) ? invoice.pop_attachments : [];
  const taxAttachments = Array.isArray(invoice?.tax_attachments) ? invoice.tax_attachments : [];

  if (!invoice?.has_pdf && popAttachments.length === 0) {
    return (
      <div>
        <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10}}>{t("Attachments")}</div>
        <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
          {t("No invoice document attached.")}
        </div>
      </div>
    );
  }

  const downloadPdf = async () => {
    setBusyKey('pdf');
    try { await downloadInvoiceDocument(invoiceId); }
    catch (e) { alert('Download failed: ' + e.message); }
    finally { setBusyKey(null); }
  };
  const downloadPop = async (att) => {
    setBusyKey(att.id);
    try { await downloadInvoiceAttachment(invoiceId, att.id, att.name); }
    catch (e) { alert('Download failed: ' + e.message); }
    finally { setBusyKey(null); }
  };

  return (
    <div>
      <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10}}>{t("Invoice document")}</div>
      {invoice?.has_pdf ? (
        <div className="file-row">
          <div className="file-icon">pdf</div>
          <div style={{flex: 1, minWidth: 0}}>
            <div className="file-name" style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
              {invoice.pdf_filename || `${invoiceId}.pdf`}
            </div>
            <div className="file-meta">
              {invoice.pdf_size ? formatBytes(invoice.pdf_size) : '—'} · {t("uploaded by vendor")}
            </div>
          </div>
          <button className="icon-btn" onClick={downloadPdf} disabled={!!busyKey} title={t("Download")}>
            {busyKey === 'pdf' ? '…' : <IconDownload size={14}/>}
          </button>
        </div>
      ) : (
        <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
          {t("No invoice document attached.")}
        </div>
      )}

      <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", margin: "16px 0 10px"}}>
        {t("Proof of performance")} {popAttachments.length > 0 && <span style={{color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0}}>· {t("{n} files", { n: popAttachments.length })}</span>}
      </div>
      {popAttachments.length === 0 ? (
        <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
          {t("No proof-of-performance files. (Legacy invoice — submitted before evidence was required.)")}
        </div>
      ) : (
        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
          {popAttachments.map(att => (
            <div className="file-row" key={att.id}>
              <div className="file-icon" style={{textTransform: "uppercase"}}>
                {(att.name?.split('.').pop() || '?').slice(0, 4)}
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="file-name" style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                  {att.name || att.id}
                </div>
                <div className="file-meta">
                  {att.size ? formatBytes(att.size) : '—'} · {t("uploaded by vendor")}
                </div>
              </div>
              <button className="icon-btn" onClick={() => downloadPop(att)} disabled={!!busyKey} title={t("Download")}>
                {busyKey === att.id ? '…' : <IconDownload size={14}/>}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ⚠ NEW (2026-07-27) — tax invoice (ใบกำกับภาษี) the supplier uploads
          after payment. Shown once the invoice is paid; downloads reuse the
          same attachment endpoint as POP files. */}
      {invoice?.status === 'paid' && (
        <>
          <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", margin: "16px 0 10px"}}>
            {t("Tax invoice")} {taxAttachments.length > 0 && <span style={{color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0}}>· {t("{n} files", { n: taxAttachments.length })}</span>}
          </div>
          {taxAttachments.length === 0 ? (
            <div style={{padding: 14, color: "var(--warning)", fontSize: 12, fontStyle: "italic"}}>
              {t("Not yet submitted by the supplier.")}
            </div>
          ) : (
            <div style={{display: "flex", flexDirection: "column", gap: 6}}>
              {taxAttachments.map(att => (
                <div className="file-row" key={att.id}>
                  <div className="file-icon" style={{textTransform: "uppercase"}}>
                    {(att.name?.split('.').pop() || '?').slice(0, 4)}
                  </div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div className="file-name" style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                      {att.name || att.id}
                    </div>
                    <div className="file-meta">
                      {att.size ? formatBytes(att.size) : '—'} · {t("uploaded by vendor")}
                      {att.uploaded_at ? ` · ${new Date(att.uploaded_at).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  <button className="icon-btn" onClick={() => downloadPop(att)} disabled={!!busyKey} title={t("Download")}>
                    {busyKey === att.id ? '…' : <IconDownload size={14}/>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


/* ============================================================
   SCREEN — SUPPLIER PORTAL: SETTINGS
   ============================================================ */
// ⚠ MODIFIED FROM PORT — accepts live `vendor` so Personal info / header show
// the real supplier on file instead of "Praew Wattana / Tinker Post".
/* ⚠ NEW (2026-05-31) — wired change-password for suppliers. Calls
 * api_changePassword (requires the current password). Replaces the old
 * cosmetic form. */
function ChangePasswordSection() {
  const t = useT();
  const [current, setCurrent] = useStateEx('');
  const [next, setNext] = useStateEx('');
  const [confirm, setConfirm] = useStateEx('');
  const [busy, setBusy] = useStateEx(false);
  const [error, setError] = useStateEx(null);

  const submit = async () => {
    setError(null);
    if (!current) { setError('Enter your current password.'); return; }
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      window.__toast?.('Password updated', 'success');
    } catch (e) {
      setError(e.message || 'Could not update password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="section">
      <div className="section-head"><h3>{t("Password")}</h3></div>
      <div className="section-body">
        <div className="form-grid">
          <div className="field">
            <label className="field-label">{t("Current password")}</label>
            <input className="input" type="password" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} disabled={busy}/>
          </div>
          <div className="field"></div>
          <div className="field">
            <label className="field-label">{t("New password")}</label>
            <input className="input" type="password" autoComplete="new-password" placeholder={t("Min. 8 characters")}
              value={next} onChange={(e) => setNext(e.target.value)} disabled={busy}/>
          </div>
          <div className="field">
            <label className="field-label">{t("Confirm new password")}</label>
            <input className="input" type="password" autoComplete="new-password" placeholder={t("Re-enter")}
              value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy}/>
          </div>
        </div>
        {error && (
          <div style={{marginTop: 12, padding: "10px 12px", background: "color-mix(in oklab, var(--danger) 12%, transparent)", color: "var(--danger)", borderRadius: 8, fontSize: 13}}>
            {error}
          </div>
        )}
        <div style={{marginTop: 12, fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.6}}>
          {t("Forgot your current password? Sign out and use “Forgot?” on the sign-in screen to email yourself a reset link.")}
        </div>
      </div>
      <div style={{padding: "12px 18px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8}}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t('Updating…') : t('Update password')}
        </button>
      </div>
    </div>
  );
}

/* ⚠ NEW (2026-05-31) — real supplier Team access (multiple logins per vendor).
 * Loads from api_listTeam; owners can invite / pause / remove. */
function TeamSection({ vendor }) {
  const t = useT();
  const [data, setData] = useStateEx(null);
  const [loading, setLoading] = useStateEx(true);
  const [err, setErr] = useStateEx(null);
  const [showInvite, setShowInvite] = useStateEx(false);
  const [invEmail, setInvEmail] = useStateEx('');
  const [invName, setInvName] = useStateEx('');
  const [invRole, setInvRole] = useStateEx('member');
  const [busy, setBusy] = useStateEx(false);
  const [actionEmail, setActionEmail] = useStateEx(null);

  const load = () => {
    setLoading(true);
    api.listTeam()
      .then(d => { setData(d); setErr(null); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, []);

  const canManage = !!(data && data.can_manage);
  const fmtWhen = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';
  const STATUS_PILL = { active: 'pill-success', invited: 'pill-warning', disabled: 'pill-neutral' };

  const submitInvite = async () => {
    if (!invEmail.trim() || !invName.trim()) { window.__toast?.('Email and name are required', 'error'); return; }
    setBusy(true);
    try {
      await api.inviteTeammate(invEmail.trim(), invName.trim(), invRole);
      window.__toast?.(`Invite sent to ${invEmail.trim()}`, 'success');
      setShowInvite(false); setInvEmail(''); setInvName(''); setInvRole('member');
      load();
    } catch (e) { window.__toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const setStatus = async (email, status) => {
    setActionEmail(email);
    try {
      await api.setTeammateStatus(email, status);
      window.__toast?.(status === 'disabled' ? 'Access paused' : 'Access restored', 'success');
      load();
    } catch (e) { window.__toast?.(e.message, 'error'); }
    finally { setActionEmail(null); }
  };
  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from your team? They'll lose access immediately.`)) return;
    setActionEmail(email);
    try {
      await api.removeTeammate(email);
      window.__toast?.('Member removed', 'success');
      load();
    } catch (e) { window.__toast?.(e.message, 'error'); }
    finally { setActionEmail(null); }
  };

  return (
    <div className="section">
      <div className="section-head">
        <h3>{t('Team members')}{data ? ` · ${data.members.length}` : ''}</h3>
        {canManage && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(s => !s)}>
            <IconPlus size={14}/> {t('Invite teammate')}
          </button>
        )}
      </div>

      {showInvite && canManage && (
        <div className="section-body" style={{borderBottom: "1px solid var(--border-subtle)"}}>
          <div className="form-grid">
            <div className="field">
              <label className="field-label">{t('Name')}</label>
              <input className="input" value={invName} onChange={e => setInvName(e.target.value)} placeholder="Colleague's name" disabled={busy}/>
            </div>
            <div className="field">
              <label className="field-label">{t('Email')}</label>
              <input className="input" type="email" value={invEmail} onChange={e => setInvEmail(e.target.value)} placeholder="colleague@company.com" disabled={busy}/>
            </div>
            <div className="field full">
              <label className="field-label">{t('Role')}</label>
              <div className="select">
                <select value={invRole} onChange={e => setInvRole(e.target.value)} disabled={busy}>
                  <option value="member">{t('Member — submit & track invoices')}</option>
                  <option value="owner">{t('Owner — also manage the team')}</option>
                </select>
              </div>
            </div>
          </div>
          <div style={{display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 12}}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowInvite(false)} disabled={busy}>{t('Cancel')}</button>
            <button className="btn btn-primary btn-sm" onClick={submitInvite} disabled={busy}>{busy ? t('Sending…') : t('Send invite')}</button>
          </div>
          <div className="muted" style={{fontSize: 12, marginTop: 8, lineHeight: 1.5}}>
            {t("They'll get an email with a link to set their own password. Their login is tied to {company}.", { company: vendor?.name || t('your company') })}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{padding: 32, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>{t('Loading team…')}</div>
      ) : err ? (
        <div style={{padding: 24, color: "var(--danger)", fontSize: 13}}>{err}</div>
      ) : (
        <table className="tbl" style={{tableLayout: "auto"}}>
          <thead>
            <tr>
              <th>{t('Member')}</th>
              <th>{t('Role')}</th>
              <th>{t('Status')}</th>
              <th>{t('Last active')}</th>
              {canManage && <th style={{width: 170}}></th>}
            </tr>
          </thead>
          <tbody>
            {data.members.map(m => (
              <tr key={m.email}>
                <td>
                  <div style={{display: "flex", alignItems: "center", gap: 10}}>
                    <Avatar name={m.name || m.email} size="sm"/>
                    <div style={{minWidth: 0}}>
                      <div style={{fontSize: 13}}>{m.name}{m.is_me && <span className="muted" style={{fontSize: 11, marginLeft: 6}}>{t('(you)')}</span>}</div>
                      <div className="mono muted ellipsis" style={{fontSize: 11}}>{m.email}</div>
                    </div>
                  </div>
                </td>
                <td><span className="pill pill-neutral" style={{fontSize: 11, textTransform: "capitalize"}}>{m.team_role}</span></td>
                <td><span className={"pill " + (STATUS_PILL[m.status] || "pill-neutral")} style={{fontSize: 11, textTransform: "capitalize"}}>{m.status}</span></td>
                <td className="muted" style={{fontSize: 12}}>
                  {m.status === 'invited' ? t('Invited {date}', { date: fmtWhen(m.created_at) }) : (m.last_login_at ? fmtWhen(m.last_login_at) : '—')}
                </td>
                {canManage && (
                  <td>
                    {!m.is_me && (
                      <div style={{display: "flex", gap: 6, justifyContent: "flex-end"}}>
                        {m.status !== 'invited' && (
                          <button className="btn btn-ghost btn-sm" disabled={actionEmail === m.email}
                            onClick={() => setStatus(m.email, m.status === 'disabled' ? 'active' : 'disabled')}>
                            {m.status === 'disabled' ? t('Restore') : t('Pause')}
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" style={{color: "var(--danger)"}} disabled={actionEmail === m.email}
                          onClick={() => remove(m.email)}>{t('Remove')}</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !err && !canManage && (
        <div className="section-body">
          <div className="muted" style={{fontSize: 12, lineHeight: 1.5}}>
            {t('Only team owners can add or remove members. Ask an owner at your company to invite a colleague.')}
          </div>
        </div>
      )}
    </div>
  );
}

export function ScreenSettings({ vendor }) {
  const t = useT();
  const [tab, setTab] = useStateEx("account");
  const v = vendor || {};
  const [notifs, setNotifs] = useStateEx({
    submitted: { email: true, line: true },
    pmreview:  { email: true, line: false },
    finance:   { email: true, line: false },
    approved:  { email: true, line: true },
    paid:      { email: true, line: true },
    disputed:  { email: true, line: true },
    weekly:    { email: true, line: false },
  });

  const Toggle = ({ on, onClick }) => (
    <button onClick={onClick}
      style={{
        width: 32, height: 18, borderRadius: 10,
        background: on ? "var(--accent)" : "var(--interactive-secondary)",
        border: "none",
        position: "relative", cursor: "pointer",
        padding: 0, flexShrink: 0,
        transition: "background 0.2s",
      }}>
      <span style={{
        position: "absolute",
        left: on ? 16 : 2, top: 2,
        width: 14, height: 14, borderRadius: "50%",
        background: "#fff",
        transition: "left 0.2s",
      }}></span>
    </button>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Settings")}</h1>
          <div className="sub">{t("Account, notifications & team access")}{v.name ? ` · ${v.name}` : ''}</div>
        </div>
      </div>

      <div className="tabs">
        {[
          ["account",  "Account"],
          ["security", "Security"],
          ["notifs",   "Notifications"],
          ["team",     "Team access"],
        ].map(([id, lbl]) => (
          <button key={id} className={"tab " + (tab === id ? "active" : "")} onClick={() => setTab(id)}>
            {t(lbl)}
          </button>
        ))}
      </div>

      <div style={{padding: "20px 24px 24px", display: "grid", gridTemplateColumns: "240px 1fr", gap: 32, maxWidth: 1100, margin: "0 auto", width: "100%"}}>
        <aside>
          <div className="t-eyebrow" style={{marginBottom: 14, fontSize: 11}}>
            {tab === "account" ? t("Your profile") :
             tab === "security" ? t("Sign-in & security") :
             tab === "notifs" ? t("How we reach you") :
             tab === "team" ? t("Who has access") :
             ""}
          </div>
          <div className="muted" style={{fontSize: 12, lineHeight: 1.6}}>
            {tab === "account"  && t("Your personal account info — visible only to your team.")}
            {tab === "security" && t("Update the password you use to sign in to the portal.")}
            {tab === "notifs"   && t("Pick how you want to be notified at each step of the invoice lifecycle.")}
            {tab === "team"     && t("Add colleagues from your company. They get their own login and the access role you choose.")}
          </div>
        </aside>

        <div>
          {tab === "account" && (
            <div style={{display: "flex", flexDirection: "column", gap: 16}}>
              <div className="section">
                <div className="section-head"><h3>{t("Personal info")}</h3></div>
                <div className="section-body">
                  <div style={{display: "flex", gap: 16, alignItems: "center", marginBottom: 18}}>
                    <Avatar name={v.contact || v.name || "Vendor"} size="lg"/>
                    <div style={{flex: 1}}>
                      <div style={{fontSize: 14, fontWeight: 400}}>{v.contact || v.name || t("Vendor contact")}</div>
                      <div className="muted" style={{fontSize: 12}}>{v.name || ''}</div>
                    </div>
                  </div>
                  <div style={{fontSize: 12, color: "var(--text-tertiary)", marginBottom: 14, lineHeight: 1.6}}>
                    {t("These details are on file in MotionReach's vendor master. To change them, contact your MotionReach representative — only admins can edit the master record.")}
                  </div>
                  <div className="form-grid">
                    <div className="field">
                      <label className="field-label">{t("Contact name")}</label>
                      <input className="input" value={v.contact || ''} disabled/>
                    </div>
                    <div className="field">
                      <label className="field-label">{t("Company")}</label>
                      <input className="input" value={v.name || ''} disabled/>
                    </div>
                    <div className="field">
                      <label className="field-label">{t("Email")}</label>
                      <input className="input" value={v.email || ''} disabled/>
                    </div>
                    <div className="field">
                      <label className="field-label">{t("Phone")}</label>
                      <input className="input" value={v.phone || ''} disabled/>
                    </div>
                    <div className="field">
                      <label className="field-label">{t("Vendor ID")}</label>
                      <input className="input mono" value={v.vendor_id || ''} disabled/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "security" && (
            <div style={{display: "flex", flexDirection: "column", gap: 16}}>
              <ChangePasswordSection/>
            </div>
          )}

          {tab === "notifs" && (
            <div className="section">
              <div className="section-head"><h3>{t("Invoice lifecycle notifications")}</h3>
                <span className="muted" style={{fontSize: 12}}>{t("Pick channels per event")}</span>
              </div>
              <table className="tbl" style={{tableLayout: "auto"}}>
                <thead>
                  <tr>
                    <th>{t("Event")}</th>
                    <th>{t("Description")}</th>
                    <th style={{width: 80, textAlign:"center"}}>{t("Email")}</th>
                    <th style={{width: 80, textAlign:"center"}}>{t("LINE")}</th>
                    <th style={{width: 100, textAlign:"center"}}>{t("In-app")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["submitted","Invoice submitted",        "When we receive your invoice from the portal"],
                    ["pmreview", "PM review complete",       "When the project manager approves or returns the invoice"],
                    ["finance",  "Finance verification",     "When Finance starts and finishes verification"],
                    ["approved", "Approved for payment",     "When your invoice is queued for the next pay run"],
                    ["paid",     "Payment released",         "When funds are transferred to your bank account"],
                    ["disputed", "Disputed or returned",     "When there's an issue we need your input on"],
                    ["weekly",   "Weekly statement",         "Friday summary of all activity"],
                  ].map(([key, label, desc]) => (
                    <tr key={key}>
                      <td style={{fontSize: 13, fontWeight: 400}}>{t(label)}</td>
                      <td className="muted" style={{fontSize: 12, whiteSpace: "normal"}}>{t(desc)}</td>
                      <td style={{textAlign: "center"}}>
                        <Toggle on={notifs[key].email} onClick={() => setNotifs({...notifs, [key]: {...notifs[key], email: !notifs[key].email}})}/>
                      </td>
                      <td style={{textAlign: "center"}}>
                        <Toggle on={notifs[key].line} onClick={() => setNotifs({...notifs, [key]: {...notifs[key], line: !notifs[key].line}})}/>
                      </td>
                      <td style={{textAlign: "center"}}>
                        <span className="muted" style={{fontSize: 11, fontFamily: "var(--font-mono)"}}>{t("always")}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "team" && <TeamSection vendor={v}/>}
        </div>
      </div>
    </div>
  );
}

